import {
  NAME_MAX,
  entryKcal,
  optionalGramsInput,
  optionalKcalPer100gInput,
  quantityInput,
  type CalorieDishDto,
  type CalorieEntryDto,
  type CalorieMealDto,
  type CalorieProductDto,
  type entryBody,
} from '@iac/contracts';
import { useId, useMemo, useState, type FormEvent } from 'react';
import type { z } from 'zod';
import { Badge } from '../../components/Badge';
import { Combobox } from '../../components/Combobox';
import { Field, SelectField } from '../../components/Field';
import { useDict } from '../../i18n/dictionary';
import { useDishes, useProducts } from './api';
import { caloriesDict } from './i18n';
import { formatQuantity, productHint } from './links';

/**
 * What the form sends — the pre-parse shape the contract accepts. `mealId`
 * only changes when the form shows the section picker (editing); adding
 * takes the section the form was opened in.
 */
export type EntryFormValues = z.input<typeof entryBody> & {
  mealId: string | null;
};

/**
 * One suggestion: a product, or a dish carrying its derived totals. Names are
 * unique across both, so the list never shows one twice.
 */
type Suggestion =
  | { kind: 'product'; item: CalorieProductDto }
  | { kind: 'dish'; item: CalorieDishDto };

const plCollator = new Intl.Collator('pl');

/** "No section" in a `<select>`, where the value has to be a string. */
const NO_MEAL = '';

const blank: EntryFormValues = {
  name: '',
  kcalPer100g: '',
  grams: '',
  quantity: '1',
  mealId: null,
};

function fromEntry(entry: CalorieEntryDto): EntryFormValues {
  return {
    mealId: entry.mealId,
    name: entry.name,
    kcalPer100g: entry.kcalPer100g === null ? '' : String(entry.kcalPer100g),
    grams: entry.grams === null ? '' : String(entry.grams),
    quantity: formatQuantity(entry.quantity),
  };
}

/** The live preview, by the same rules the server applies. */
function previewKcal(values: EntryFormValues): number | null {
  const kcalPer100g = optionalKcalPer100gInput.safeParse(values.kcalPer100g);
  const grams = optionalGramsInput.safeParse(values.grams);
  const quantity = quantityInput.safeParse(values.quantity);
  if (!kcalPer100g.success || !grams.success || !quantity.success) return null;
  return entryKcal({
    kcalPer100g: kcalPer100g.data,
    grams: grams.data,
    quantity: quantity.data,
  });
}

/**
 * Add and edit share this form. Only the name is required; an entry missing
 * a number is saved as it is and counts no kcal until finished. Picking a
 * catalog product or dish fills its numbers, which stay editable.
 */
export function EntryForm({
  entry,
  meals,
  submitLabel,
  pending,
  onSubmit,
  onCancel,
}: {
  entry?: CalorieEntryDto;
  /** Shows the section picker (when there is at least one section). */
  meals?: CalorieMealDto[];
  submitLabel: string;
  pending: boolean;
  onSubmit: (values: EntryFormValues, reset: () => void) => void;
  onCancel: () => void;
}) {
  const formId = useId();
  const t = useDict(caloriesDict);
  const { data: products } = useProducts();
  const { data: dishes } = useDishes();
  const suggestions = useMemo<Suggestion[]>(
    () =>
      [
        ...(products ?? []).map((item) => ({
          kind: 'product' as const,
          item,
        })),
        ...(dishes ?? []).map((item) => ({ kind: 'dish' as const, item })),
      ].sort((a, b) => plCollator.compare(a.item.name, b.item.name)),
    [products, dishes],
  );
  const [values, setValues] = useState<EntryFormValues>(() =>
    entry ? fromEntry(entry) : blank,
  );

  const patch = (next: Partial<EntryFormValues>) =>
    setValues((current) => ({ ...current, ...next }));

  /** A dish fills its totals, the way a product fills its own numbers. */
  function pick({ item }: Suggestion) {
    patch({
      name: item.name,
      kcalPer100g: item.kcalPer100g === null ? '' : String(item.kcalPer100g),
      grams: item.grams === null ? '' : String(item.grams),
    });
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit(values, () => setValues(blank));
  }

  const kcal = previewKcal(values);

  return (
    <form onSubmit={submit} className="entry-form">
      <div className="entry-form-fields">
        <div className="entry-form-name">
          <Combobox
            id={`${formId}-name`}
            label={t.product}
            value={values.name}
            onChange={(name) => patch({ name })}
            options={suggestions}
            optionKey={(s) => s.item.id}
            optionLabel={(s) => s.item.name}
            renderOption={(s) => (
              <>
                <span>
                  {s.item.name}
                  {s.kind === 'dish' && (
                    <>
                      {' '}
                      <Badge tone="neutral">{t.dishMarker}</Badge>
                      {s.item.incomplete && (
                        <>
                          {' '}
                          <Badge tone="warning">{t.dishIncomplete}</Badge>
                        </>
                      )}
                    </>
                  )}
                </span>
                <span className="text-hint">{productHint(s.item)}</span>
              </>
            )}
            onPick={pick}
            newLabel={t.addNew}
            required
            maxLength={NAME_MAX}
            autoFocus={!entry}
          />
        </div>
        <Field
          id={`${formId}-kcal`}
          label="kcal/100g"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={values.kcalPer100g}
          onChange={(e) => patch({ kcalPer100g: e.target.value })}
        />
        <Field
          id={`${formId}-grams`}
          label={t.weight}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={values.grams}
          onChange={(e) => patch({ grams: e.target.value })}
        />
        <Field
          id={`${formId}-quantity`}
          label={t.quantity}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          required
          value={values.quantity}
          onChange={(e) => patch({ quantity: e.target.value })}
        />
        {meals && meals.length > 0 && (
          <div className="entry-form-meal">
            <SelectField
              id={`${formId}-meal`}
              label={t.meal}
              value={values.mealId ?? NO_MEAL}
              onChange={(e) =>
                patch({
                  mealId: e.target.value === NO_MEAL ? null : e.target.value,
                })
              }
            >
              {/* Not a choice: only an entry already outside every section
                  shows it, until a section is picked. */}
              {values.mealId === null && (
                <option value={NO_MEAL} disabled>
                  {t.pickMeal}
                </option>
              )}
              {meals.map((meal) => (
                <option key={meal.id} value={meal.id}>
                  {meal.name}
                </option>
              ))}
            </SelectField>
          </div>
        )}
      </div>

      <div className="entry-form-footer">
        <span className="entry-form-total">
          {kcal === null ? t.noKcalData : `${kcal} kcal`}
        </span>
        <div className="form-actions">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            {t.cancel}
          </button>
          <button type="submit" className="btn-primary" disabled={pending}>
            {submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}
